// Fenêtre phrase/photo ouverte sur la page Sillages, redessinée dans la
// photo 30x30 cm : le rendu 3D ne contient que le canvas WebGL, le panneau
// est du HTML posé par-dessus. On reprend sa place et ses styles réels à
// l'écran (getBoundingClientRect / getComputedStyle), agrandis à la taille
// de la photo. La punchline (SVG) est redessinée en vectoriel, la photo
// depuis l'original HD. La croix et la navigation ‹ 1/3 › (commandes de la
// page) ne sont pas reprises. Le cadre dessiné est recentré dans la photo
// (la page le centre déjà dans le viseur, navigation comprise).

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Image illisible : ${src}`));
    img.src = src;
  });
}

// Le SVG est sérialisé à la taille finale en pixels : sans ça, certains
// navigateurs le pixellisent à sa taille d'origine puis l'agrandissent (flou).
async function drawSvg(ctx, svgEl, x, y, w, h) {
  const clone = svgEl.cloneNode(true);
  if (!clone.getAttribute('viewBox')) {
    const width = parseFloat(svgEl.getAttribute('width')) || svgEl.getBoundingClientRect().width;
    const height = parseFloat(svgEl.getAttribute('height')) || svgEl.getBoundingClientRect().height;
    clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(Math.round(w)));
  clone.setAttribute('height', String(Math.round(h)));
  clone.removeAttribute('style');
  const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    ctx.drawImage(await loadImage(url), x, y, w, h);
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Texte d'une ligne centré dans son cadre, espacement des lettres compris
// (dessiné lettre par lettre : ctx.letterSpacing n'existe pas partout).
async function drawText(ctx, el, rect, scale) {
  const style = getComputedStyle(el);
  const fontSize = parseFloat(style.fontSize) * scale;
  const font = `${style.fontStyle} ${style.fontWeight} ${fontSize}px ${style.fontFamily}`;
  try {
    await document.fonts.load(font);
  } catch {
    // Police indisponible : celle de secours du navigateur.
  }
  let text = el.textContent.trim();
  if (style.textTransform === 'uppercase') text = text.toUpperCase();
  const spacing = (parseFloat(style.letterSpacing) || 0) * scale;
  ctx.font = font;
  ctx.fillStyle = style.color;
  ctx.textBaseline = 'middle';
  const chars = [...text];
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total = widths.reduce((sum, w) => sum + w, 0) + spacing * (chars.length - 1);
  let x = rect.x + (rect.w - total) / 2;
  const y = rect.y + rect.h / 2;
  chars.forEach((c, i) => {
    ctx.fillText(c, x, y);
    x += widths[i] + spacing;
  });
}

/**
 * Dessine le panneau ouvert (s'il l'est) sur la photo.
 * @param {CanvasRenderingContext2D} ctx  canvas de la photo (size x size)
 * @param {object} options
 * @param {DOMRect} options.frame  carré du viseur à l'écran
 * @param {number} options.size  côté de la photo en pixels
 * @param {HTMLElement} options.backdrop  fond assombri du panneau
 * @param {HTMLElement} options.panel  le panneau lui-même
 * @param {string|null} options.photoUrl  original HD de la photo affichée
 * @param {number} [options.panelScale]  mise à l'échelle CSS (transform) du
 *   panneau dans le viseur : getComputedStyle ne la reflète pas
 */
export async function drawPanelOverlay(ctx, { frame, size, backdrop, panel, photoUrl, panelScale = 1 }) {
  if (backdrop.hidden) return;
  const scale = size / frame.width;
  const styleScale = scale * panelScale;
  const toPhoto = (r) => ({
    x: (r.left - frame.left) * scale,
    y: (r.top - frame.top) * scale,
    w: r.width * scale,
    h: r.height * scale,
  });

  ctx.save();
  ctx.fillStyle = getComputedStyle(backdrop).backgroundColor;
  ctx.fillRect(0, 0, size, size);

  const panelStyle = getComputedStyle(panel);
  const box = toPhoto(panel.getBoundingClientRect());
  // Sans la navigation ‹ 1/3 ›, le cadre s'arrête sous le dernier contenu
  // dessiné, avec la même marge intérieure que sur la page.
  const navRow = panel.querySelector('.popup-nav-row');
  const lastContent = [...panel.querySelectorAll('.popup-svg-wrap svg, .popup-photo-wrap img, .popup-pseudo')].pop();
  if (navRow && lastContent) {
    const bottom = toPhoto(lastContent.getBoundingClientRect());
    box.h = bottom.y + bottom.h + parseFloat(panelStyle.paddingBottom) * styleScale - box.y;
  }
  ctx.translate((size - box.w) / 2 - box.x, (size - box.h) / 2 - box.y);
  ctx.fillStyle = panelStyle.backgroundColor;
  ctx.fillRect(box.x, box.y, box.w, box.h);
  const border = parseFloat(panelStyle.borderTopWidth) * styleScale;
  if (border > 0) {
    ctx.strokeStyle = panelStyle.borderTopColor;
    ctx.lineWidth = border;
    ctx.strokeRect(box.x + border / 2, box.y + border / 2, box.w - border, box.h - border);
  }

  const svgEl = panel.querySelector('.popup-svg-wrap svg');
  if (svgEl) {
    const r = toPhoto(svgEl.getBoundingClientRect());
    await drawSvg(ctx, svgEl, r.x, r.y, r.w, r.h);
  }

  const imgEl = panel.querySelector('.popup-photo-wrap img');
  if (imgEl) {
    const r = toPhoto(imgEl.getBoundingClientRect());
    let source = imgEl;
    if (photoUrl) {
      try {
        source = await loadImage(photoUrl);
      } catch (error) {
        console.error('[sillage] Original HD indisponible, version web utilisée', error);
      }
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, r.x, r.y, r.w, r.h);
  }

  const pseudoEl = panel.querySelector('.popup-pseudo');
  if (pseudoEl) await drawText(ctx, pseudoEl, toPhoto(pseudoEl.getBoundingClientRect()), styleScale);
  ctx.restore();
}
